import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';
import { renderMarkdown } from '../../utils/renderMarkdown';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

// ============================================================
// Mobile hook
// ============================================================

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
};

// ============================================================
// Типы
// ============================================================

interface EclipseAspect {
  planet: string;
  aspect: string;
  orb: number;
}

interface EclipseData {
  date: string;
  type: 'solar' | 'lunar';
  sign: string;
  degree: number;
  visibility: 'visible' | 'not_visible';
  aspectToNatal: EclipseAspect[];
}

interface EclipseForecast {
  upcomingEclipses: EclipseData[];
  impact: string;
  recommendations: string[];
  warnings: string[];
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

// ============================================================
// Константы
// ============================================================

const SIGN_NAMES_RU: Record<string, string> = {
  Aries: 'Овен',
  Taurus: 'Телец',
  Gemini: 'Близнецы',
  Cancer: 'Рак',
  Leo: 'Лев',
  Virgo: 'Дева',
  Libra: 'Весы',
  Scorpio: 'Скорпион',
  Sagittarius: 'Стрелец',
  Capricorn: 'Козерог',
  Aquarius: 'Водолей',
  Pisces: 'Рыбы',
};

const COLORS = {
  bg: '#0a0814',
  cardBg: 'rgba(13, 20, 40, 0.85)',
  cardBorder: 'rgba(212, 175, 55, 0.12)',
  cardShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
  gold: '#d4af37',
  goldLight: '#f4e4a6',
  text: '#f0f0f0',
  textMuted: '#b0b0b0',
  danger: '#ff6b6b',
  success: '#4caf50',
  solar: '#f4a460',
  lunar: '#87ceeb',
};

const goldAccentLine = (
  <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
);

const headingStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

// ============================================================
// Компонент
// ============================================================

const EclipseForecastPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const [forecast, setForecast] = useState<EclipseForecast | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEclipse, setSelectedEclipse] = useState<string | null>(null);

  const loadForecast = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<ApiResponse<EclipseForecast>>('/eclipses/forecast');
      if (res.data.success && res.data.data) {
        setForecast(res.data.data);
      } else {
        setError(res.data.message || 'Не удалось загрузить прогноз');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки прогноза';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadForecast();
    }
  }, [isAuthenticated]);

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getAspectColor = (aspect: string): string => {
    switch (aspect) {
      case 'Соединение': return COLORS.gold;
      case 'Трин':
      case 'Секстиль': return COLORS.success;
      case 'Квадрат':
      case 'Оппозиция': return COLORS.danger;
      default: return COLORS.textMuted;
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: COLORS.gold,
        fontSize: '18px',
        fontFamily: 'var(--font-sans)',
      }}>
        Войдите, чтобы увидеть прогноз затмений
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: COLORS.gold,
        fontSize: '18px',
        fontFamily: 'var(--font-sans)',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌑</div>
          <div>Рассчитываем влияние затмений...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        maxWidth: '800px',
        margin: '40px auto',
        padding: '24px',
        background: 'rgba(255, 107, 107, 0.1)',
        border: `1px solid ${COLORS.danger}`,
        borderRadius: '12px',
        color: COLORS.danger,
        fontFamily: 'var(--font-sans)',
      }}>
        <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Ошибка</div>
        <div>{error}</div>
        <button
          onClick={loadForecast}
          style={{
            marginTop: '16px',
            padding: '8px 20px',
            background: 'linear-gradient(135deg, #d4af37, #f4d03f)',
            color: '#0A0814',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: 600,
            fontFamily: 'var(--font-sans)',
          }}
        >
          Повторить
        </button>
      </div>
    );
  }

  if (!forecast) {
    return (
      <div style={{
        maxWidth: '800px',
        margin: '40px auto',
        padding: '24px',
        textAlign: 'center',
        color: COLORS.textMuted,
        fontFamily: 'var(--font-sans)',
      }}>
        Нет данных о затмениях
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '900px',
      margin: '0 auto',
      padding: isMobile ? '16px 12px' : '24px 16px',
      fontFamily: 'var(--font-sans)',
      color: COLORS.text,
      position: 'relative',
    }}>
      {goldAccentLine}

      {/* Заголовок */}
      <div style={{
        textAlign: 'center',
        marginBottom: isMobile ? '24px' : '40px',
      }}>
        <h1 style={{
          ...headingStyle,
          fontSize: isMobile ? '24px' : '32px',
          margin: '0 0 8px 0',
        }}>
          ☀️ Прогноз затмений 🌙
        </h1>
        <p style={{
          color: COLORS.textMuted,
          fontSize: isMobile ? '13px' : '15px',
          margin: 0,
        }}>
          Влияние солнечных и лунных затмений на вашу натальную карту
        </p>
      </div>

      {/* Список затмений */}
      <div style={{ marginBottom: isMobile ? '24px' : '32px' }}>
        <h2 style={{
          ...headingStyle,
          fontSize: isMobile ? '18px' : '22px',
          marginBottom: '16px',
        }}>
          Ближайшие затмения
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: '16px',
        }}>
          {forecast.upcomingEclipses.map((eclipse) => {
            const signRu = SIGN_NAMES_RU[eclipse.sign] ?? eclipse.sign;
            const isSolar = eclipse.type === 'solar';
            const icon = isSolar ? '☀️' : '🌙';
            const typeLabel = isSolar ? 'Солнечное' : 'Лунное';
            const borderColor = isSolar ? COLORS.solar : COLORS.lunar;
            const isSelected = selectedEclipse === eclipse.date;

            return (
              <div
                key={eclipse.date}
                onClick={() => setSelectedEclipse(isSelected ? null : eclipse.date)}
                style={{
                  background: isSelected
                    ? `linear-gradient(135deg, ${borderColor}22 0%, ${COLORS.cardBg} 100%)`
                    : COLORS.cardBg,
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${isSelected ? borderColor : COLORS.cardBorder}`,
                  borderRadius: '24px',
                  padding: isMobile ? '12px' : '16px',
                  cursor: 'pointer',
                  boxShadow: COLORS.cardShadow,
                  transition: 'border-color 0.2s, background 0.2s',
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                }}>
                  <span style={{ fontSize: '24px' }}>{icon}</span>
                  <div>
                    <div style={{
                      fontWeight: 600,
                      color: borderColor,
                      fontSize: '14px',
                    }}>
                      {typeLabel} затмение
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: COLORS.textMuted,
                    }}>
                      {formatDate(eclipse.date)}
                    </div>
                  </div>
                </div>

                <div style={{
                  fontSize: '15px',
                  color: COLORS.goldLight,
                  marginBottom: '8px',
                }}>
                  {signRu} {eclipse.degree}°
                </div>

                {eclipse.aspectToNatal.length > 0 && (
                  <div>
                    <div style={{
                      fontSize: '12px',
                      color: COLORS.textMuted,
                      marginBottom: '4px',
                    }}>
                      Аспекты ({eclipse.aspectToNatal.length}):
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {eclipse.aspectToNatal.slice(0, 3).map((a, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: '11px',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: `${getAspectColor(a.aspect)}22`,
                            color: getAspectColor(a.aspect),
                            border: `1px solid ${getAspectColor(a.aspect)}44`,
                          }}
                        >
                          {a.planet} {a.aspect}
                        </span>
                      ))}
                      {eclipse.aspectToNatal.length > 3 && (
                        <span style={{
                          fontSize: '11px',
                          color: COLORS.textMuted,
                        }}>
                          +{eclipse.aspectToNatal.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Детали выбранного затмения */}
      {selectedEclipse && (
        <div style={{
          background: COLORS.cardBg,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${COLORS.cardBorder}`,
          borderRadius: '24px',
          padding: isMobile ? '16px' : '20px',
          marginBottom: isMobile ? '24px' : '32px',
          boxShadow: COLORS.cardShadow,
          position: 'relative',
        }}>
          {goldAccentLine}
          {(() => {
            const eclipse = forecast.upcomingEclipses.find((e) => e.date === selectedEclipse);
            if (!eclipse) return null;
            const signRu = SIGN_NAMES_RU[eclipse.sign] ?? eclipse.sign;
            const isSolar = eclipse.type === 'solar';
            const borderColor = isSolar ? COLORS.solar : COLORS.lunar;

            return (
              <div>
                <h3 style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '18px',
                  color: borderColor,
                  marginBottom: '16px',
                }}>
                  {isSolar ? '☀️' : '🌙'} {isSolar ? 'Солнечное' : 'Лунное'} затмение — {signRu} {eclipse.degree}°
                </h3>

                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '13px', color: COLORS.textMuted, marginBottom: '8px' }}>
                    Дата: <span style={{ color: COLORS.text }}>{formatDate(eclipse.date)}</span>
                  </div>
                  <div style={{ fontSize: '13px', color: COLORS.textMuted, marginBottom: '8px' }}>
                    Тип: <span style={{ color: COLORS.text }}>{isSolar ? 'Солнечное' : 'Лунное'}</span>
                  </div>
                  <div style={{ fontSize: '13px', color: COLORS.textMuted, marginBottom: '8px' }}>
                    Видимость: <span style={{ color: eclipse.visibility === 'visible' ? COLORS.success : COLORS.textMuted }}>
                      {eclipse.visibility === 'visible' ? 'Видно в вашем регионе' : 'Не видно в вашем регионе'}
                    </span>
                  </div>
                </div>

                {eclipse.aspectToNatal.length > 0 && (
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: COLORS.goldLight,
                      marginBottom: '8px',
                    }}>
                      Аспекты к натальным планетам:
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {eclipse.aspectToNatal.map((a, i) => (
                        <div
                          key={i}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '6px 12px',
                            background: `${getAspectColor(a.aspect)}11`,
                            borderRadius: '6px',
                            border: `1px solid ${getAspectColor(a.aspect)}33`,
                          }}
                        >
                          <span style={{ color: COLORS.text, fontSize: '13px' }}>{a.planet}</span>
                          <span style={{
                            color: getAspectColor(a.aspect),
                            fontSize: '13px',
                            fontWeight: 500,
                          }}>
                            {a.aspect}
                          </span>
                          <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>
                            орбис {a.orb}°
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {eclipse.aspectToNatal.length === 0 && (
                  <div style={{
                    fontSize: '13px',
                    color: COLORS.textMuted,
                    fontStyle: 'italic',
                  }}>
                    Нет точных аспектов к вашим натальным планетам
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* AI-интерпретация */}
      {forecast.impact && (
        <div style={{
          background: COLORS.cardBg,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${COLORS.cardBorder}`,
          borderRadius: '24px',
          padding: isMobile ? '16px' : '24px',
          marginBottom: isMobile ? '24px' : '32px',
          boxShadow: COLORS.cardShadow,
          position: 'relative',
        }}>
          {goldAccentLine}
          <h2 style={{
            ...headingStyle,
            fontSize: isMobile ? '18px' : '22px',
            marginBottom: '16px',
            marginTop: 0,
          }}>
            ✨ Интерпретация
          </h2>
          <div
            style={{
              color: COLORS.text,
              fontSize: '14px',
              lineHeight: 1.7,
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(renderMarkdown(forecast.impact)),
            }}
          />
        </div>
      )}

      {/* Предостережения */}
      {forecast.warnings.length > 0 && (
        <div style={{
          background: 'rgba(255, 107, 107, 0.08)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${COLORS.danger}44`,
          borderRadius: '24px',
          padding: isMobile ? '16px' : '20px',
          marginBottom: '24px',
          boxShadow: COLORS.cardShadow,
          position: 'relative',
        }}>
          {goldAccentLine}
          <h3 style={{
            ...headingStyle,
            fontSize: isMobile ? '16px' : '18px',
            margin: '0 0 12px 0',
          }}>
            ⚠️ Предостережения
          </h3>
          <ul style={{ margin: 0, paddingLeft: '20px', color: COLORS.text, fontSize: '14px', lineHeight: 1.8 }}>
            {forecast.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Рекомендации */}
      {forecast.recommendations.length > 0 && (
        <div style={{
          background: 'rgba(76, 175, 80, 0.08)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${COLORS.success}44`,
          borderRadius: '24px',
          padding: isMobile ? '16px' : '20px',
          marginBottom: isMobile ? '24px' : '32px',
          boxShadow: COLORS.cardShadow,
          position: 'relative',
        }}>
          {goldAccentLine}
          <h3 style={{
            ...headingStyle,
            fontSize: isMobile ? '16px' : '18px',
            margin: '0 0 12px 0',
          }}>
            ✅ Рекомендации
          </h3>
          <ul style={{ margin: 0, paddingLeft: '20px', color: COLORS.text, fontSize: '14px', lineHeight: 1.8 }}>
            {forecast.recommendations.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Кнопка обновления */}
      <div style={{ textAlign: 'center' }}>
        <button
          onClick={loadForecast}
          disabled={loading}
          style={{
            padding: isMobile ? '10px 20px' : '10px 28px',
            background: 'linear-gradient(135deg, #d4af37, #f4d03f)',
            color: '#0A0814',
            border: 'none',
            borderRadius: '12px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 700,
            fontSize: isMobile ? '13px' : '14px',
            fontFamily: 'var(--font-sans)',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Загрузка...' : 'Обновить прогноз'}
        </button>
      </div>
    </div>
  );
};

export default EclipseForecastPage;
